import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';

import { PhoneField } from '@/components/PhoneField/PhoneField';
import { TextField } from '@/components/TextField';
import { Textarea } from '@/components/Textarea';

// Shared shell with ExpressInterestModal — reuse the same CSS to avoid drift.
import '@/components/ExpressInterestModal/ExpressInterestModal.css';

const E164_REGEX = /^\+[1-9]\d{1,14}$/;

export const CALLBACK_SLOTS = ['09-12', '12-15', '15-18', '18-21'] as const;
export type CallbackSlot = (typeof CALLBACK_SLOTS)[number];

export type CallbackModalSubmitValues = {
  fullName: string;
  email: string;
  phone: string;
  message: string;
  slot: CallbackSlot;
  callbackDate: string; // ISO YYYY-MM-DD
};

export type RequestCallbackModalProps = {
  open: boolean;
  dealId: string;
  dealTitle: string;
  referenceId: string;
  priceLabel: string;
  prefilledProfile: { fullName: string; email: string; phone: string } | null;
  onClose: () => void;
  onSubmit: (values: CallbackModalSubmitValues) => Promise<void>;
};

// Window allowed by DB CHECK buyer_requests_callback_date_window: today through
// today + 14 days inclusive. Returned in ISO YYYY-MM-DD so <input type="date">
// `min` / `max` attributes accept them directly.
function callbackDateWindow(): { min: string; max: string } {
  const today = new Date();
  const isoDate = (date: Date) => date.toISOString().slice(0, 10);
  const min = isoDate(today);
  const maxDate = new Date(today);
  maxDate.setDate(maxDate.getDate() + 14);
  const max = isoDate(maxDate);
  return { min, max };
}

function callbackSchema(t: TFunction, dateWindow: { min: string; max: string }) {
  return z.object({
    fullName: z
      .string()
      .trim()
      .min(2, t('deal.modal.validation.fullNameRequired'))
      .max(140, t('deal.modal.validation.fullNameTooLong')),
    email: z
      .string()
      .trim()
      .toLowerCase()
      .min(1, t('deal.modal.validation.emailRequired'))
      .email(t('deal.modal.validation.emailInvalid'))
      .max(254, t('deal.modal.validation.emailInvalid')),
    phone: z
      .string()
      .trim()
      .min(1, t('deal.modal.validation.phoneRequired'))
      .regex(E164_REGEX, t('deal.modal.validation.phoneInvalidE164')),
    message: z.string().trim().max(500, t('deal.modal.validation.messageTooLong')),
    slot: z.enum(CALLBACK_SLOTS, t('deal.modal.validation.slotRequired')),
    callbackDate: z
      .string()
      .min(1, t('deal.modal.validation.dateRequired'))
      .refine(
        (s) => s >= dateWindow.min && s <= dateWindow.max,
        t('deal.modal.validation.dateOutOfWindow'),
      ),
  });
}

type FormValues = z.infer<ReturnType<typeof callbackSchema>>;

export function RequestCallbackModal({
  open,
  dealTitle,
  referenceId,
  priceLabel,
  prefilledProfile,
  onClose,
  onSubmit,
}: RequestCallbackModalProps) {
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const dateWindow = useMemo(callbackDateWindow, []);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormValues>({
    resolver: zodResolver(callbackSchema(t, dateWindow)),
    defaultValues: {
      fullName: prefilledProfile?.fullName ?? '',
      email: prefilledProfile?.email ?? '',
      phone: prefilledProfile?.phone ?? '',
      message: '',
      slot: '09-12',
      callbackDate: dateWindow.min,
    },
  });

  useEffect(() => {
    if (!open) return;
    closeButtonRef.current?.focus();
    setSubmitError(null);
    reset({
      fullName: prefilledProfile?.fullName ?? '',
      email: prefilledProfile?.email ?? '',
      phone: prefilledProfile?.phone ?? '',
      message: '',
      slot: '09-12',
      callbackDate: dateWindow.min,
    });
  }, [open, prefilledProfile, reset, dateWindow.min]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isSubmitting) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, isSubmitting, onClose]);

  if (!open) return null;

  const readOnlyPrefill = prefilledProfile !== null;

  const submit = handleSubmit(async (values) => {
    setSubmitError(null);
    try {
      await onSubmit(values);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t('buyer.errors.generic'));
    }
  });

  return (
    <div
      className="confirm-modal__backdrop"
      role="presentation"
      onClick={() => {
        if (!isSubmitting) onClose();
      }}
    >
      <div
        className="confirm-modal conversion-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="callback-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="callback-modal-title" className="confirm-modal__title">
          {t('deal.modal.callbackTitle')}
        </h2>
        <p className="conversion-modal__summary">
          {t('deal.modal.summary', {
            id: referenceId,
            title: dealTitle,
            price: priceLabel,
          })}
        </p>

        <form onSubmit={submit} className="conversion-modal__form" noValidate>
          <TextField
            label={t('deal.modal.fullName')}
            error={errors.fullName?.message}
            readOnly={readOnlyPrefill}
            {...register('fullName')}
          />
          <TextField
            label={t('deal.modal.email')}
            type="email"
            error={errors.email?.message}
            readOnly={readOnlyPrefill}
            {...register('email')}
          />
          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState }) => (
              <PhoneField
                name="phone"
                label={t('deal.modal.phone')}
                placeholder="50 123 4567"
                required
                value={field.value ?? ''}
                onChange={field.onChange}
                error={fieldState.error?.message}
              />
            )}
          />

          <TextField
            label={t('deal.modal.callbackDate')}
            type="date"
            error={errors.callbackDate?.message}
            helper={t('deal.modal.callbackDateHelper')}
            min={dateWindow.min}
            max={dateWindow.max}
            {...register('callbackDate')}
          />

          <div className="m5-field">
            <span className="m5-field__label">{t('deal.modal.slot')}</span>
            <Controller
              control={control}
              name="slot"
              render={({ field }) => (
                <div className="conversion-modal__slot-group" role="radiogroup">
                  {CALLBACK_SLOTS.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      role="radio"
                      aria-checked={field.value === slot}
                      className={`conversion-modal__slot${
                        field.value === slot ? ' is-active' : ''
                      }`}
                      onClick={() => field.onChange(slot)}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
              )}
            />
            {errors.slot ? (
              <span className="m5-field__error" role="alert">
                {errors.slot.message}
              </span>
            ) : null}
          </div>

          <Textarea
            label={t('deal.modal.message')}
            placeholder={t('deal.modal.messagePlaceholder')}
            error={errors.message?.message}
            helper={t('deal.modal.messageHelper')}
            rows={3}
            {...register('message')}
          />

          {submitError ? (
            <p role="alert" className="conversion-modal__error">
              {submitError}
            </p>
          ) : null}

          <div className="confirm-modal__actions">
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="confirm-modal__btn confirm-modal__btn--cancel"
            >
              {t('deal.modal.cancel')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="confirm-modal__btn confirm-modal__btn--confirm"
            >
              {isSubmitting ? t('deal.modal.sending') : t('deal.modal.send')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
